// dependency-graph-ignore-file

import { Box, xcss } from '@atlaskit/primitives';
import usePanelState from '@databases/panel/usePanelState';
import { type ResolvedValue } from '@databases/selections-api';
import { getIsSortingActive } from '@databases/utils/database/view/view';
import useView from '@databases/views/useView';
import { isEqual } from 'lodash-es';
import React, { useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSnapshot } from 'valtio';

import { ActionBarContent } from '../ActionBarContent';

import {
  actionBarPortalState,
  type PartialActionBarProps,
} from './action-bar-portal-state';
import { useDeleteFieldsAction } from './hooks/use-delete-fields-action';

/**
 * Computes the initial value for bulk editing.
 * Returns the common value only when a single field is selected and all values are equal.
 */
function getInitialValueForBulkEdit(
  selectedValues: readonly ResolvedValue[],
  fieldsWithSelectionsCount: number
) {
  if (fieldsWithSelectionsCount !== 1 || selectedValues.length === 0) {
    return undefined;
  }

  const firstValue = selectedValues[0].value;
  const allEqual = selectedValues.every((v) => isEqual(v.value, firstValue));

  return allEqual ? firstValue : undefined;
}

const actionBarContainerStyles = xcss({
  position: 'fixed',
  width: 'max-content',
  zIndex: 'modal',
  left: '50%',
  transform: 'translate(-50%)',
  bottom: '54px',
  cursor: 'default',
  height: 'space.500',
});

/**
 * Inner component that's only mounted when the action bar should be visible.
 * Uses hooks without worrying about conditional rendering.
 * Actions are pre-computed by the effect for simplicity.
 */
const ActionBarPortalContent = ({
  partialProps,
}: {
  partialProps: PartialActionBarProps;
}) => {
  const { view, setViewChange } = useView();
  const { isPanelVisible } = usePanelState();
  const { contentId, selectionsApiFactory, panelManager } = partialProps;

  const hasSortedFields = getIsSortingActive(view.settings);
  const isEntryPanelActive = isPanelVisible('EntryViewPanel');
  const selectionsApi = useMemo(() => selectionsApiFactory(), [selectionsApiFactory]);

  // Resolve actions - if it's a factory (column mode), call with setViewChange from hook
  const actions = useMemo(() => {
    if (typeof partialProps.actions === 'function') {
      return partialProps.actions(setViewChange);
    }

    return partialProps.actions;
  }, [partialProps, setViewChange]);

  const handleDeleteFields = useDeleteFieldsAction({
    selectionsApi,
    panelManager,
    contentId,
  });

  const handleOnCloseActionBar = useCallback(() => {
    selectionsApi.deselectAll();
  }, [selectionsApi]);

  const initialValue = getInitialValueForBulkEdit(
    partialProps.selectedValues,
    partialProps.fieldsWithSelectionsCount
  );

  // Merge partial props from effect with hook-derived props
  const fullProps = {
    ...partialProps,
    actions,
    handleDeleteFields,
    handleOnCloseActionBar,
    hasSortedFields,
    initialValue,
    isEntryPanelActive,
  };

  return (
    <Box
      id="database-action-bar-container"
      testId="action-bar-container"
      xcss={actionBarContainerStyles}
    >
      <ActionBarContent {...fullProps} />
    </Box>
  );
};

/**
 * React Portal that mounts to document.body when visible.
 * Context flows through the React tree, so full access to providers is preserved.
 *
 * Note: The effect that toggles this visibility is NEVER run unless our experiment / the
 * new selections system is enabled. So, this is safe to put inside the app without any
 * additional gating around it.
 */
export const ActionBarPortal = () => {
  const { isVisible, props } = useSnapshot(actionBarPortalState);

  if (!isVisible || !props) {
    return null;
  }

  return createPortal(
    <ActionBarPortalContent partialProps={props as PartialActionBarProps} />,
    document.body
  );
};
